import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { PRODUCTS } from "@/lib/catalog";
import { ASSORTIMENT_CATEGORIES } from "@/lib/assortiment-categories";
import { trackEvent } from "@/lib/analytics";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/shop/ProductCard";
import { ContactCta } from "@/components/site/ContactCta";
import { CategoryCards } from "@/components/site/CategoryCards";
import uitverkoopThermometer480 from "@/assets/uitverkoop-thermometer-480.webp.asset.json";
import uitverkoopThermometer768 from "@/assets/uitverkoop-thermometer-768.webp.asset.json";
import uitverkoopThermometer1200 from "@/assets/uitverkoop-thermometer-1200.webp.asset.json";
import uitverkoopThermometer1600 from "@/assets/uitverkoop-thermometer-1600.webp.asset.json";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import {
  
  ChevronLeft,
  ChevronRight,
  Search,
  SlidersHorizontal,
  X,
  ChevronDown,
} from "lucide-react";

// Geen .default() gebruiken: dan zou de router /assortiment doorsturen (307)
// naar /assortiment?categorie=&q=&page=1&... Crawlers krijgen dan onnodig een
// redirect op de belangrijkste shoppagina. Alles is optioneel; de component
// vult zelf de standaardwaarden in.
const assortimentSearchSchema = z.object({
  categorie: fallback(z.string(), "").optional(),
  q: fallback(z.string(), "").optional(),
  page: fallback(z.number().int(), 1).optional(),
  specs: fallback(z.string(), "").optional(),
  max: fallback(z.number(), 0).optional(),
  sort: fallback(z.string(), "").optional(),
});

// Serialize/parse helpers voor URL-sync van filters. Zo werken browser
// back/forward correct: bij elke navigatie leidt de URL de state af.
function parseCategorieParam(value: string | undefined): Set<string> {
  if (!value) return new Set();
  const slugs = value.split(",").map((s) => s.trim()).filter(Boolean);
  const valid = new Set(FILTER_GROUPS.map((g) => g.slug));
  return new Set(slugs.filter((s) => valid.has(s)));
}
function serializeCategorie(set: Set<string>): string | undefined {
  if (set.size === 0) return undefined;
  return Array.from(set).join(",");
}
function parseSpecsParam(value: string | undefined): Record<string, Set<string>> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as Record<string, string[]>;
    const out: Record<string, Set<string>> = {};
    for (const [label, vals] of Object.entries(parsed)) {
      if (Array.isArray(vals) && vals.length > 0) out[label] = new Set(vals);
    }
    return out;
  } catch {
    return {};
  }
}
function serializeSpecs(specs: Record<string, Set<string>>): string | undefined {
  const entries = Object.entries(specs).filter(([, v]) => v.size > 0);
  if (entries.length === 0) return undefined;
  const obj: Record<string, string[]> = {};
  for (const [label, v] of entries) obj[label] = Array.from(v).sort();
  return JSON.stringify(obj);
}
function specsEqual(a: Record<string, Set<string>>, b: Record<string, Set<string>>) {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    const va = a[k];
    const vb = b[k];
    if (!vb || va.size !== vb.size) return false;
    for (const val of va) if (!vb.has(val)) return false;
  }
  return true;
}
function setsEqual(a: Set<string>, b: Set<string>) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

export const Route = createFileRoute("/assortiment")({
  validateSearch: zodValidator(assortimentSearchSchema),
  head: () => {
    // Canonical wijst ALTIJD naar de schone /assortiment URL zonder query
    // parameters (?page=, ?categorie=, ?q=, ?specs=, ?max=, ?sort=). Zo
    // consolideren we alle SEO-signalen op één canonieke pagina en
    // voorkomen we dat gepagineerde/gefilterde varianten als duplicate
    // content worden gezien.
    const CANONICAL = "https://elvion-group.nl/assortiment";
    return {
      meta: [
        { title: "Assortiment | Elvion Group Nederland" },
        {
          name: "description",
          content:
            "Bekijk het assortiment van Elvion Group Nederland: IP-camera's, groepenkasten, netwerken en verlichting van A-merken uit voorraad.",
        },
        { property: "og:title", content: "Assortiment | Elvion Group Nederland" },
        {
          property: "og:description",
          content:
            "Het assortiment van Elvion Group Nederland: A-merken uit voorraad.",
        },
        { property: "og:url", content: CANONICAL },
      ],
      links: [{ rel: "canonical", href: CANONICAL }],
      scripts: [breadcrumbJsonLd([{ name: "Assortiment", path: "/assortiment" }])],
    };
  },
  component: UitverkoopPage,
});

const PER_PAGE = 12;

/*
  De volgorde die de gebruiker wil:
  1. Camera's  (ip-cameras, ptz-camera)
  2. Recorders (netwerk-recorders)
  3. Beugels   (bevestigingsbeugels)
  4. Opslag    (opslagmedia – niet in sale)
  5. Kabels    (data-kabel – niet in sale)
*/
const FILTER_GROUPS = ASSORTIMENT_CATEGORIES;

type SortValue =
  | "category"
  | "price-asc"
  | "price-desc"
  | "name-asc"
  | "name-desc"
  | "discount-desc";

const SORT_OPTIONS: { value: SortValue; label: string; short: string }[] = [
  { value: "category", label: "Categorie (standaard)", short: "Categorie" },
  { value: "price-asc", label: "Prijs: laag → hoog", short: "Prijs: laag → hoog" },
  { value: "price-desc", label: "Prijs: hoog → laag", short: "Prijs: hoog → laag" },
  { value: "discount-desc", label: "Korting: hoog → laag", short: "Korting: hoog → laag" },
  { value: "name-asc", label: "Naam: A → Z", short: "Naam: A → Z" },
  { value: "name-desc", label: "Naam: Z → A", short: "Naam: Z → A" },
];

function UitverkoopPage() {
  const { categorie, q, page: pageParam, specs: specsParam, max: maxParam, sort: sortParam } = Route.useSearch();
  const navigate = useNavigate({ from: "/assortiment" });
  const [searchQuery, setSearchQuery] = useState(q?.trim() ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<Set<string>>(() => parseCategorieParam(categorie));
  const [activeSpecs, setActiveSpecs] = useState<Record<string, Set<string>>>(() => parseSpecsParam(specsParam));
  const [maxPrice, setMaxPrice] = useState<number | null>(() => (maxParam && maxParam > 0 ? maxParam : null));
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["Categorie"]));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const validSort = (v: string): v is SortValue => SORT_OPTIONS.some((o) => o.value === v);
  const [sortOption, setSortOption] = useState<SortValue>(() =>
    sortParam && validSort(sortParam) ? sortParam : "category",
  );
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const sortButtonRef = useRef<HTMLButtonElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const [sortFocusIndex, setSortFocusIndex] = useState(0);
  const [tempMounted, setTempMounted] = useState(false);
  useEffect(() => { setTempMounted(true); }, []);
  // De Bilt temperatuur — gecached via TanStack Query (10 min fresh + 10 min refresh)
  // en gepersisteerd in localStorage zodat we tussen sessies geen onnodige API calls doen.
  const CACHE_KEY = "debilt-temp-cache-v1";
  const TEN_MIN = 10 * 60 * 1000;
  const cached = (() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { temp: number; ts: number };
      if (typeof parsed?.temp !== "number" || typeof parsed?.ts !== "number") return null;
      return parsed;
    } catch {
      return null;
    }
  })();

  const { data: deBiltTemp = null } = useQuery<number | null>({
    queryKey: ["debilt-temperature"],
    queryFn: async ({ signal }) => {
      const r = await fetch(
        "https://api.open-meteo.com/v1/forecast?latitude=52.1&longitude=5.18&current=temperature_2m&timezone=Europe/Amsterdam",
        { signal },
      );
      const d = await r.json();
      const t = d?.current?.temperature_2m;
      if (typeof t !== "number") return null;
      try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify({ temp: t, ts: Date.now() }));
      } catch {}
      return t;
    },
    enabled: tempMounted,
    initialData: cached?.temp ?? undefined,
    initialDataUpdatedAt: cached?.ts,
    staleTime: 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const tempEuros = deBiltTemp !== null ? Math.max(0, Math.round(deBiltTemp)) : null;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Houd URL in sync met de zoekopdracht zodat deeplinks en de sitelinks
  // search box (SearchAction) werken. TanStack Router past percent-encoding
  // toe op de search-waarde, dus spaties en speciale tekens (é, &, +, /, …)
  // komen correct in de URL. We normaliseren eerst whitespace zodat de
  // gegenereerde URL altijd schoon is.
  useEffect(() => {
    const normalized = searchQuery.replace(/\s+/g, " ").trim();
    const current = (q ?? "").replace(/\s+/g, " ").trim();
    if (normalized === current) return;
    navigate({
      search: (prev: { categorie?: string; q?: string; page?: number }) => ({
        ...prev,
        q: normalized || undefined,
        page: undefined,
      }),
      replace: true,
      resetScroll: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // URL → state: als de URL verandert (bv. via browser back/forward of een
  // externe deeplink), leiden we de lokale filter-state uit de URL af. Zo
  // toont /assortiment altijd exact wat er in de URL staat.
  useEffect(() => {
    const urlFilters = parseCategorieParam(categorie);
    if (!setsEqual(urlFilters, activeFilters)) setActiveFilters(urlFilters);
    const urlSpecs = parseSpecsParam(specsParam);
    if (!specsEqual(urlSpecs, activeSpecs)) setActiveSpecs(urlSpecs);
    const urlMax = maxParam && maxParam > 0 ? maxParam : null;
    if (urlMax !== maxPrice) setMaxPrice(urlMax);
    const urlSort: SortValue = sortParam && validSort(sortParam) ? sortParam : "category";
    if (urlSort !== sortOption) setSortOption(urlSort);
    const urlQ = (q ?? "").replace(/\s+/g, " ").trim();
    const stateQ = searchQuery.replace(/\s+/g, " ").trim();
    if (urlQ !== stateQ) setSearchQuery(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorie, specsParam, maxParam, sortParam, q]);

  // State → URL: filterwijzigingen worden in de URL gepusht met replace:false
  // zodat browser back/forward tussen filterstates werkt. Pagina reset naar 1
  // wordt door een aparte effect afgehandeld.
  useEffect(() => {
    const nextCat = serializeCategorie(activeFilters);
    const nextSpecs = serializeSpecs(activeSpecs);
    const nextMax = maxPrice !== null && maxPrice > 0 ? maxPrice : undefined;
    const nextSort = sortOption === "category" ? undefined : sortOption;
    const curCat = categorie || undefined;
    const curSpecs = specsParam || undefined;
    const curMax = maxParam && maxParam > 0 ? maxParam : undefined;
    const curSort = sortParam && sortParam !== "category" ? sortParam : undefined;
    if (nextCat === curCat && nextSpecs === curSpecs && nextMax === curMax && nextSort === curSort) return;
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        categorie: nextCat,
        specs: nextSpecs,
        max: nextMax,
        sort: nextSort,
      }),
      replace: false,
      resetScroll: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, activeSpecs, maxPrice, sortOption]);



  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Producten laden vanuit de statische catalogus. Hoewel dit synchroon is
  // tonen we een korte skeleton-state (voor de eerste render/hydration) en
  // een duidelijke foutmelding als de catalogus onverwacht leeg of ongeldig
  // is, zodat browsen op /assortiment altijd duidelijk blijft.
  const [productsStatus, setProductsStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [productsError, setProductsError] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (!Array.isArray(PRODUCTS) || PRODUCTS.length === 0) {
        setProductsError("De productcatalogus kon niet worden geladen.");
        setProductsStatus("error");
        return;
      }
      setProductsStatus("ready");
    } catch (err) {
      setProductsError(
        err instanceof Error ? err.message : "Onbekende fout bij het laden van producten.",
      );
      setProductsStatus("error");
    }
  }, []);

  const sale = useMemo(
    () =>
      PRODUCTS.filter(
        (p) =>
          typeof p.old_price_cents === "number" &&
          p.old_price_cents > p.list_price_cents,
      ).sort((a, b) => {
        const da = (b.old_price_cents! - b.list_price_cents) / b.old_price_cents!;
        const db = (a.old_price_cents! - a.list_price_cents) / a.old_price_cents!;
        return da - db;
      }),
    [],
  );

  // Basisset na zoekopdracht (gedeeld door alle telmacro's)
  const searched = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return sale;
    return sale.filter((p) => {
      const haystack = [
        p.name,
        p.category_label,
        p.category,
        p.spec_tag ?? "",
        p.subcategory ?? "",
        ...(p.pros ?? []),
        ...(p.cons ?? []),
      ]
        .join(" \u0001 ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sale, debouncedQuery]);

  // Prijsgrenzen (euro) afgeleid van de saleset
  const priceBounds = useMemo(() => {
    if (sale.length === 0) return { min: 0, max: 0 };
    const prices = sale.map((p) => Math.round(p.list_price_cents / 100));
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [sale]);
  const effectiveMaxPrice = maxPrice ?? priceBounds.max;
  const priceActive = maxPrice !== null && maxPrice < priceBounds.max;

  // Helper: pas categorie-, spec- en prijsfilters toe, optioneel een groep overslaan
  const applyFilters = (
    items: typeof sale,
    opts: { skipCategory?: boolean; skipSpec?: string; skipPrice?: boolean } = {},
  ) => {
    let res = items;
    if (!opts.skipCategory && activeFilters.size > 0) {
      const allowedSubs = new Set<string>();
      FILTER_GROUPS.forEach((g) => {
        if (activeFilters.has(g.slug)) {
          g.subcategories.forEach((s) => allowedSubs.add(s));
        }
      });
      res = res.filter((p) => allowedSubs.has(p.subcategory || ""));
    }
    for (const [label, values] of Object.entries(activeSpecs)) {
      if (opts.skipSpec === label) continue;
      if (values.size === 0) continue;
      res = res.filter((p) => {
        const match = p.specs?.find((s) => s.label === label);
        return match ? values.has(match.value) : false;
      });
    }
    if (!opts.skipPrice && priceActive) {
      res = res.filter((p) => Math.round(p.list_price_cents / 100) <= effectiveMaxPrice);
    }
    return res;
  };

  // Categorie-tellingen: passen alle andere actieve filters toe (spec + prijs)
  const groupCounts = useMemo(() => {
    const base = applyFilters(searched, { skipCategory: true });
    const counts = new Map<string, number>();
    FILTER_GROUPS.forEach((g) => {
      counts.set(
        g.slug,
        base.filter((p) => g.subcategories.includes(p.subcategory || "")).length,
      );
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, activeFilters, activeSpecs, effectiveMaxPrice, priceActive]);

  // Beschikbare spec-opties (uit volledige sale-set, statisch)
  const specOptions = useMemo(() => {
    const specs: Record<string, Set<string>> = {};
    for (const p of sale) {
      for (const s of p.specs ?? []) {
        if (!s.value || s.value === "—") continue;
        if (!specs[s.label]) specs[s.label] = new Set();
        specs[s.label].add(s.value);
      }
    }
    return Object.entries(specs)
      .filter(([, v]) => v.size >= 2)
      .map(([label, v]) => ({
        label,
        values: Array.from(v).sort(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sale]);

  // Spec-tellingen per label: alle andere actieve filters toepassen,
  // zodat de badges tonen wat je krijgt als je die waarde aanzet.
  const specCountsFiltered = useMemo(() => {
    const counts: Record<string, Map<string, number>> = {};
    for (const { label } of specOptions) {
      const base = applyFilters(searched, { skipSpec: label });
      const map = new Map<string, number>();
      for (const p of base) {
        const match = p.specs?.find((s) => s.label === label);
        if (!match) continue;
        map.set(match.value, (map.get(match.value) ?? 0) + 1);
      }
      counts[label] = map;
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specOptions, searched, activeFilters, activeSpecs, effectiveMaxPrice, priceActive]);

  const filtered = useMemo(
    () => applyFilters(searched),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searched, activeFilters, activeSpecs, effectiveMaxPrice, priceActive],
  );

  // Track zoekopdrachten in analytics (alleen bij daadwerkelijke zoekterm).
  const lastTrackedSearch = useRef<string | null>(null);
  useEffect(() => {
    const term = debouncedQuery.trim();
    if (!term || term === lastTrackedSearch.current) return;
    lastTrackedSearch.current = term;
    trackEvent("search", {
      search_term: term,
      result_count: filtered.length,
      page_path: typeof window !== "undefined" ? window.location.pathname : "",
    });
  }, [debouncedQuery, filtered.length]);

  // Sorteer op de gekozen optie
  const sorted = useMemo(() => {
    const items = [...filtered];
    switch (sortOption) {
      case "price-asc":
        return items.sort((a, b) => a.list_price_cents - b.list_price_cents);
      case "price-desc":
        return items.sort((a, b) => b.list_price_cents - a.list_price_cents);
      case "name-asc":
        return items.sort((a, b) => a.name.localeCompare(b.name));
      case "name-desc":
        return items.sort((a, b) => b.name.localeCompare(a.name));
      case "discount-desc": {
        return items.sort((a, b) => {
          const da = a.old_price_cents
            ? (a.old_price_cents - a.list_price_cents) / a.old_price_cents
            : 0;
          const db = b.old_price_cents
            ? (b.old_price_cents - b.list_price_cents) / b.old_price_cents
            : 0;
          return db - da;
        });
      }
      case "category":
      default: {
        const orderMap = new Map<string, number>();
        FILTER_GROUPS.forEach((g, i) => {
          g.subcategories.forEach((s) => orderMap.set(s, i));
        });
        return items.sort((a, b) => {
          const oa = orderMap.get(a.subcategory || "") ?? 999;
          const ob = orderMap.get(b.subcategory || "") ?? 999;
          return oa - ob;
        });
      }
    }
  }, [filtered, sortOption]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const page = Math.min(Math.max(1, pageParam ?? 1), totalPages);

  const setPage = (n: number) => {
    navigate({
      search: (prev: { categorie?: string; q?: string; page?: number }) => ({
        ...prev,
        page: n <= 1 ? undefined : n,
      }),
      replace: false,
      resetScroll: false,
    });
  };

  useEffect(() => {
    if ((pageParam ?? 1) !== 1) {
      navigate({
        search: (prev: { categorie?: string; q?: string; page?: number }) => ({
          ...prev,
          page: undefined,
        }),
        replace: true,
        resetScroll: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, activeFilters, activeSpecs, effectiveMaxPrice, priceActive]);

  const safePage = page;
  const start = (safePage - 1) * PER_PAGE;
  const pageItems = sorted.slice(start, start + PER_PAGE);

  const toggleFilter = (slug: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
    setMobileFiltersOpen(false);
  };

  const toggleSpec = (label: string, value: string) => {
    setActiveSpecs((prev) => {
      const next = { ...prev };
      const cur = new Set(next[label] ?? []);
      if (cur.has(value)) cur.delete(value);
      else cur.add(value);
      if (cur.size === 0) delete next[label];
      else next[label] = cur;
      return next;
    });
    setMobileFiltersOpen(false);
  };

  const clearFilters = () => {
    setActiveFilters(new Set());
    setActiveSpecs({});
    setMaxPrice(null);
  };

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const activeCount =
    activeFilters.size +
    Object.values(activeSpecs).reduce((sum, set) => sum + set.size, 0) +
    (priceActive ? 1 : 0);

  // Gedeeld filterpaneel (mobiel + desktop) — blauw thema, accordion-stijl
  const renderFilterPanel = () => (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-bold text-foreground">Filters</h3>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
          >
            Wis alles
          </button>
        )}
      </div>

      {/* Prijs */}
      <div className="border-t border-border py-4">
        <div className="mb-3 text-sm font-semibold text-foreground">Prijs</div>
        <input
          type="range"
          min={priceBounds.min}
          max={priceBounds.max}
          value={effectiveMaxPrice}
          onChange={(e) => {
            setMaxPrice(Number(e.target.value));
            setMobileFiltersOpen(false);
          }}
          className="w-full cursor-pointer accent-blue-600"
          aria-label="Maximale prijs"
        />
        <div className="mt-2 flex items-center justify-between text-xs text-text-secondary">
          <span>€{priceBounds.min}</span>
          <span>Max: €{effectiveMaxPrice}</span>
        </div>
      </div>

      {/* Categorie-accordion */}
      <div className="border-t border-border">
        <button
          type="button"
          onClick={() => toggleSection("Categorie")}
          className="flex w-full items-center justify-between py-3 text-left text-sm font-semibold text-foreground"
          aria-expanded={expandedSections.has("Categorie")}
        >
          <span>Categorie</span>
          <ChevronDown
            className={`h-4 w-4 text-text-secondary transition-transform ${
              expandedSections.has("Categorie") ? "rotate-180" : ""
            }`}
          />
        </button>
        {expandedSections.has("Categorie") && (
          <div className="space-y-2 pb-3">
            {FILTER_GROUPS.map((g) => {
              const count = groupCounts.get(g.slug) ?? 0;
              const isActive = activeFilters.has(g.slug);
              const disabled = count === 0 && !isActive;
              return (
                <label
                  key={g.slug}
                  className={`flex items-center justify-between text-sm ${
                    disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => toggleFilter(g.slug)}
                      disabled={disabled}
                      className="h-4 w-4 cursor-pointer rounded border-border accent-blue-600"
                    />
                    <span className={isActive ? "font-medium text-foreground" : "text-foreground"}>
                      {g.label}
                    </span>
                  </span>
                  <span className="text-xs text-text-secondary">({count})</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Spec-accordions */}
      {specOptions.map((spec) => {
        const open = expandedSections.has(spec.label);
        return (
          <div key={spec.label} className="border-t border-border">
            <button
              type="button"
              onClick={() => toggleSection(spec.label)}
              className="flex w-full items-center justify-between py-3 text-left text-sm font-semibold text-foreground"
              aria-expanded={open}
            >
              <span>{spec.label}</span>
              <ChevronDown
                className={`h-4 w-4 text-text-secondary transition-transform ${
                  open ? "rotate-180" : ""
                }`}
              />
            </button>
            {open && (
              <div className="space-y-2 pb-3">
                {spec.values.map((v) => {
                  const isActive = !!activeSpecs[spec.label]?.has(v);
                  const count = specCountsFiltered[spec.label]?.get(v) ?? 0;
                  const disabled = count === 0 && !isActive;
                  return (
                    <label
                      key={v}
                      className={`flex items-center justify-between text-sm ${
                        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={() => toggleSpec(spec.label, v)}
                          disabled={disabled}
                          className="h-4 w-4 cursor-pointer rounded border-border accent-blue-600"
                        />
                        <span className={isActive ? "font-medium text-foreground" : "text-foreground"}>
                          {v}
                        </span>
                      </span>
                      <span className="text-xs text-text-secondary">({count})</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );


  return (
    <>
      <Header variant="shop" />
      <main className="bg-background">
        <h1 className="sr-only">Assortiment: beveiligingscamera's, recorders en intercoms</h1>
        {/* Mobiel + tablet: alleen de afbeelding, geen tekst */}
        <section className="bg-background py-[18px] lg:hidden">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-[#bcd0ef] shadow-sm [container-type:inline-size]">
              <img
                src={uitverkoopThermometer1200.url}
                srcSet={`${uitverkoopThermometer480.url} 480w, ${uitverkoopThermometer768.url} 768w, ${uitverkoopThermometer1200.url} 1200w, ${uitverkoopThermometer1600.url} 1600w`}
                sizes="100vw"
                alt="Temperatuurkorting De Bilt actie"
                className="block h-auto w-full"
                loading="eager"
                fetchPriority="high"
                width={1264}
                height={848}
              />
              <div
                className="pointer-events-none absolute flex items-center justify-center font-display font-extrabold leading-none text-white drop-shadow-md"
                style={{
                  left: "37.5%",
                  top: "51.5%",
                  transform: "translate(calc(-50% + 4px), calc(-50% + 1px)) rotate(-2deg)",
                  width: "22%",
                  height: "12%",
                  fontSize: "calc(clamp(2rem, 11cqw, 5rem) - 3px)",
                }}
              >
                {tempMounted && tempEuros !== null ? `€${tempEuros}` : "€–"}
              </div>
            </div>
          </div>
        </section>

        {/* Desktop: volledige banner met tekst */}
        <div className="hidden lg:block">
          <ContactCta
            imageUrl={uitverkoopThermometer1200.url}
            imageSrcSet={`${uitverkoopThermometer480.url} 480w, ${uitverkoopThermometer768.url} 768w, ${uitverkoopThermometer1200.url} 1200w, ${uitverkoopThermometer1600.url} 1600w`}
            imageSizes="50vw"
            imageAlt="Temperatuurkorting De Bilt actie"
            imageClassName="absolute inset-0 h-full w-full object-cover"
            imageWrapperClassName="relative aspect-[3/2] md:aspect-auto md:min-h-full bg-[#bcd0ef] [container-type:inline-size]"
            imageLoading="eager"
            imageFetchPriority="high"
            imageWidth={1264}
            imageHeight={848}
            title="Temperatuurkorting"
            titleColor="#428fde"
            primaryCtaColor="#428fde"
            description="Hoe warmer het in De Bilt, hoe hoger uw korting. U krijgt €1 korting per graad Celsius boven het vriespunt — direct gekoppeld aan de live temperatuur."
            bullets={[
              "Live gekoppeld aan de actuele temperatuur in De Bilt",
              "€1 korting per graad boven 0 °C",
              "Geldig op alle uitverkoop-producten",
            ]}
            imageOverlay={
              <div
                className="pointer-events-none absolute flex items-center justify-center font-display font-extrabold leading-none text-white drop-shadow-md"
                style={{
                  left: "37.5%",
                  top: "51.5%",
                  transform: "translate(calc(-50% + 4px), calc(-50% + 1px)) rotate(-2deg)",
                  width: "22%",
                  height: "12%",
                  fontSize: "calc(clamp(2rem, 11cqw, 5rem) - 3px)",
                }}
              >
                {tempMounted && tempEuros !== null ? `€${tempEuros}` : "€–"}
              </div>
            }
          />
        </div>

        {/* Categorie-cards (Camera's / Recorders / Intercom) onder de banner */}
        <CategoryCards
          activeSlugs={activeFilters}
          counts={groupCounts}
          onToggle={toggleFilter}
        />

        <section className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:py-5 lg:px-8">
          {/* Toolbar: filter + sorteer */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground lg:hidden"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filter
                {activeCount > 0 && (
                  <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-orange px-1.5 text-[10px] font-bold text-white">
                    {activeCount}
                  </span>
                )}
              </button>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm text-text-secondary underline underline-offset-2 lg:hidden"
                >
                  Wis filter
                </button>
              )}

            </div>

            <div className="relative lg:hidden" ref={sortRef}>
              <button
                ref={sortButtonRef}
                type="button"
                onClick={() => {
                  setSortOpen((v) => {
                    const next = !v;
                    if (next) {
                      const idx = SORT_OPTIONS.findIndex((o) => o.value === sortOption);
                      setSortFocusIndex(idx >= 0 ? idx : 0);
                    }
                    return next;
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    const idx = SORT_OPTIONS.findIndex((o) => o.value === sortOption);
                    setSortFocusIndex(idx >= 0 ? idx : 0);
                    setSortOpen(true);
                  }
                }}
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
                aria-label={`Sorteren: ${SORT_OPTIONS.find((o) => o.value === sortOption)?.short ?? ""}. Klik om te wijzigen.`}
                className="inline-flex items-center gap-2 rounded-full border border-orange bg-orange/10 px-3 py-1.5 text-sm font-semibold text-orange transition hover:bg-orange/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2"
              >
                <span>{SORT_OPTIONS.find((o) => o.value === sortOption)?.short}</span>
                <ChevronDown
                  aria-hidden="true"
                  className={`h-4 w-4 transition ${sortOpen ? "rotate-180" : ""}`}
                />
              </button>

              {sortOpen && (
                <div
                  ref={sortMenuRef}
                  role="listbox"
                  aria-label="Sorteeropties"
                  tabIndex={-1}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setSortOpen(false);
                      sortButtonRef.current?.focus();
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setSortFocusIndex((i) => (i + 1) % SORT_OPTIONS.length);
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setSortFocusIndex((i) => (i - 1 + SORT_OPTIONS.length) % SORT_OPTIONS.length);
                    } else if (e.key === "Home") {
                      e.preventDefault();
                      setSortFocusIndex(0);
                    } else if (e.key === "End") {
                      e.preventDefault();
                      setSortFocusIndex(SORT_OPTIONS.length - 1);
                    } else if (e.key === "Tab") {
                      setSortOpen(false);
                    }
                  }}
                  className="absolute right-0 z-10 mt-2 w-56 rounded-lg border border-border bg-card p-1 shadow-lg focus:outline-none"
                >
                  {SORT_OPTIONS.map((opt, i) => {
                    const isActive = sortOption === opt.value;
                    const isFocused = sortFocusIndex === i;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        tabIndex={isFocused ? 0 : -1}
                        ref={(el) => {
                          if (el && isFocused && sortOpen) el.focus();
                        }}
                        onClick={() => {
                          setSortOption(opt.value);
                          setSortOpen(false);
                          sortButtonRef.current?.focus();
                        }}
                        onMouseEnter={() => setSortFocusIndex(i)}
                        className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition focus:outline-none ${
                          isActive
                            ? "bg-orange/10 font-semibold text-orange"
                            : "text-foreground"
                        } ${isFocused ? "bg-muted" : ""}`}
                      >
                        {opt.label}
                        {isActive && (
                          <span aria-hidden="true" className="inline-flex h-2 w-2 rounded-full bg-orange" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>


          {/* Mobiele filterpaneel */}
          {mobileFiltersOpen && (
            <div className="mb-6 rounded-2xl border border-border bg-card p-4 lg:hidden">
              <div className="mb-3 flex items-center justify-between">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
                  <input
                    type="text"
                    placeholder="Zoek op naam of type..."
                    aria-label="Zoek op productnaam of type"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-4 text-sm text-foreground placeholder:text-text-secondary focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="ml-2 text-text-secondary"
                  aria-label="Sluit filters"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {renderFilterPanel()}
            </div>
          )}

          <div className="flex gap-8">
            {/* Desktop sidebar */}
            <aside className="hidden w-64 shrink-0 lg:block">
              <div className="sticky top-24 rounded-2xl border border-border bg-card p-5">
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
                    <input
                      type="text"
                      placeholder="Zoek op naam of type..."
                    aria-label="Zoek op productnaam of type"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-4 text-sm text-foreground placeholder:text-text-secondary focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                    />
                  </div>
                </div>
                {renderFilterPanel()}
              </div>
            </aside>



            {/* Productgrid */}
            <div className="min-w-0 flex-1">
              {q?.trim() && (
                <h2 className="mb-4 text-lg font-semibold text-foreground">
                  Resultaten voor “{q.trim()}”
                  <span className="ml-2 text-sm font-normal text-text-secondary">
                    ({sorted.length} {sorted.length === 1 ? "product" : "producten"})
                  </span>
                </h2>
              )}
              {productsStatus === "loading" ? (
                <ProductGridSkeleton />
              ) : productsStatus === "error" ? (
                <div
                  role="alert"
                  className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800"
                >
                  <p className="mb-2 font-semibold">Producten laden is mislukt</p>
                  <p className="mb-4">
                    {productsError ??
                      "Er ging iets mis bij het ophalen van de producten. Probeer het opnieuw."}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof window !== "undefined") window.location.reload();
                    }}
                    className="inline-flex h-10 items-center rounded-xl border border-red-300 bg-white px-4 text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    Opnieuw proberen
                  </button>
                </div>
              ) : sorted.length === 0 ? (
                <p className="rounded-md border border-border bg-card p-6 text-sm text-text-secondary">
                  {debouncedQuery || activeCount > 0
                    ? "Geen producten gevonden voor je zoekopdracht."
                    : "Er zijn op dit moment geen aanbiedingen. Kom binnenkort terug."}
                </p>
              ) : (
                <>
                  {/* Mobiel / tablet: gepagineerd, 2 kolommen */}
                  <div className="lg:hidden">
                    <div className="grid grid-cols-2 gap-4">
                      {pageItems.map((p) => (
                        <ProductCard
                          key={p.slug}
                          product={p}
                          highlight={debouncedQuery}
                          discountCents={tempMounted && tempEuros !== null ? tempEuros * 100 : undefined}
                        />
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <Pagination
                        page={safePage}
                        totalPages={totalPages}
                        onChange={(n) => {
                          setPage(n);
                          if (typeof window !== "undefined") {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                        }}
                      />
                    )}
                  </div>

                  {/* Desktop: gepagineerd, 3 kolommen */}
                  <div className="hidden lg:block">
                    <div className="grid grid-cols-3 gap-4">
                      {pageItems.map((p) => (
                        <ProductCard
                          key={p.slug}
                          product={p}
                          highlight={debouncedQuery}
                          discountCents={tempMounted && tempEuros !== null ? tempEuros * 100 : undefined}
                        />
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <Pagination
                        page={safePage}
                        totalPages={totalPages}
                        onChange={(n) => {
                          setPage(n);
                          if (typeof window !== "undefined") {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                        }}
                      />
                    )}
                  </div>

                  {/* Resultatenteller onder de grid */}
                  <p className="mt-4 text-center text-xs text-text-secondary">
                    {sorted.length === 0
                      ? "Geen resultaten"
                      : `${start + 1}–${Math.min(start + PER_PAGE, sorted.length)} van ${sorted.length} producten`}
                  </p>
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (n: number) => void;
}) {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return (
    <nav
      aria-label="Paginatie"
      className="mt-10 flex items-center justify-center gap-2"
    >
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        aria-label="Vorige pagina"
        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card text-text-secondary transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {pages.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-current={n === page ? "page" : undefined}
          className={`inline-flex h-10 min-w-10 items-center justify-center rounded-md border px-3 text-sm font-semibold transition ${
            n === page
              ? "border-orange bg-orange text-white"
              : "border-border bg-card text-foreground hover:border-foreground/40"
          }`}
        >
          {n}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        aria-label="Volgende pagina"
        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card text-text-secondary transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </nav>
  );
}

function ProductGridSkeleton() {
  return (
    <div aria-busy="true" aria-live="polite" aria-label="Producten worden geladen">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse overflow-hidden rounded-2xl border border-border bg-card"
          >
            <div className="aspect-square w-full bg-muted" />
            <div className="space-y-3 p-4">
              <div className="h-3 w-1/3 rounded bg-muted" />
              <div className="h-4 w-4/5 rounded bg-muted" />
              <div className="h-4 w-3/5 rounded bg-muted" />
              <div className="mt-4 flex items-center justify-between">
                <div className="h-5 w-20 rounded bg-muted" />
                <div className="h-9 w-24 rounded-lg bg-muted" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <span className="sr-only">Producten worden geladen…</span>
    </div>
  );
}
